import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2, _templateObject3;
/* eslint-disable @atlaskit/design-system/prefer-primitives, @atlaskit/design-system/consistent-css-prop-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx, css } from '@emotion/react';
import { N40 } from '@atlaskit/theme/colors';
import React, { useState } from 'react';
import { renderExtension } from './extension';
import { WidthConsumer, sharedMultiBodiedExtensionStyles } from '@atlaskit/editor-common/ui';
import { RendererCssClassName } from '../../consts';
import { calcBreakoutWidth } from '@atlaskit/editor-common/utils';
import { useMultiBodiedExtensionActions } from './multiBodiedExtension/actions';
import { useMultiBodiedExtensionContext } from './multiBodiedExtension/context';
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Ignored via go/DSP-18766
var navigationStyles = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\t", ";\n\tmargin-left: 0 !important;\n\tmargin-right: 0 !important;\n\t.mbe-add-tab-button,\n\t.mbe-remove-tab {\n\t\tdisplay: none;\n\t}\n"])), sharedMultiBodiedExtensionStyles.mbeNavigation);

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- needs manual remediation
var containerStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n\t", ";\n\t.ak-renderer-extension {\n\t\tmargin-top: 0 !important;\n\t}\n\n\t[data-layout='full-width'],\n\t[data-layout='wide'] {\n\t\t.multiBodiedExtension-navigation {\n\t\t\tborder-right: 3px solid ", " !important;\n\t\t}\n\t}\n"])), sharedMultiBodiedExtensionStyles.mbeExtensionContainer, "var(--ds-border, ".concat(N40, ")"));
var MultiBodiedExtensionChildrenContainer = function MultiBodiedExtensionChildrenContainer(_ref) {
  var children = _ref.children;
  return jsx("article", {
    "data-testid": "multiBodiedExtension--frames"
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "multiBodiedExtension--frames"
  }, children);
};
var MultiBodiedExtensionNavigation = function MultiBodiedExtensionNavigation(_ref2) {
  var children = _ref2.children;
  return jsx("nav", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: "multiBodiedExtension-navigation",
    css: navigationStyles,
    "data-testid": "multiBodiedExtension-navigation"
  }, children);
};
var MultiBodiedExtensionWrapper = function MultiBodiedExtensionWrapper(_ref3) {
  var width = _ref3.width,
    path = _ref3.path,
    layout = _ref3.layout,
    children = _ref3.children;
  var isTopLevel = path.length < 1;
  var centerAlignClass = isTopLevel && ['wide', 'full-width'].includes(layout) ? RendererCssClassName.EXTENSION_CENTER_ALIGN : '';
  return jsx("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: "".concat(RendererCssClassName.EXTENSION, " ").concat(centerAlignClass, " ").concat(RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER),
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      width: isTopLevel ? calcBreakoutWidth(layout, width) : '100%'
    },
    "data-layout": layout,
    "data-testid": "multiBodiedExtension--wrapper"
  }, children);
};
var MultiBodiedExtension = function MultiBodiedExtension(props) {
  var _extensionContext$pri;
  var children = props.children,
    _props$layout = props.layout,
    layout = _props$layout === void 0 ? 'default' : _props$layout,
    _props$path = props.path,
    path = _props$path === void 0 ? [] : _props$path,
    parameters = props.parameters,
    extensionType = props.extensionType,
    extensionKey = props.extensionKey,
    content = props.content,
    marks = props.marks,
    localId = props.localId;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    activeChildIndex = _useState2[0],
    setActiveChildIndex = _useState2[1];
  var _useMultiBodiedExtens = useMultiBodiedExtensionContext({
      extensionType: extensionType,
      extensionKey: extensionKey
    }),
    loading = _useMultiBodiedExtens.loading,
    extensionContext = _useMultiBodiedExtens.extensionContext;
  var actions = useMultiBodiedExtensionActions({
    updateActiveChild: setActiveChildIndex,
    children: children,
    childrenContainer: jsx(MultiBodiedExtensionChildrenContainer, null, children),
    allowBodiedOverride: extensionContext === null || extensionContext === void 0 || (_extensionContext$pri = extensionContext.privateProps) === null || _extensionContext$pri === void 0 ? void 0 : _extensionContext$pri.__allowBodiedOverride
  });
  var renderContent = React.useCallback(function () {
    var _marks$find;
    if (loading || !extensionContext) {
      return null;
    }
    var NodeRenderer = extensionContext.NodeRenderer,
      privateProps = extensionContext.privateProps;
    var fragmentLocalId = marks === null || marks === void 0 || (_marks$find = marks.find(function (m) {
      return m.type.name === 'fragment';
    })) === null || _marks$find === void 0 || (_marks$find = _marks$find.attrs) === null || _marks$find === void 0 ? void 0 : _marks$find.localId;
    var node = {
      type: 'multiBodiedExtension',
      extensionKey: extensionKey,
      extensionType: extensionType,
      parameters: parameters,
      content: content,
      localId: localId,
      fragmentLocalId: fragmentLocalId
    };
    var renderMultiBodiedExtension = function renderMultiBodiedExtension() {
      var MultiBodiedExtensionNodeRenderer = jsx(NodeRenderer, {
        node: node,
        actions: actions
      });
      try {
        if ( /*#__PURE__*/React.isValidElement(MultiBodiedExtensionNodeRenderer)) {
          // Return the content directly if it's a valid JSX.Element
          return renderExtension(MultiBodiedExtensionNodeRenderer, layout, {
            isTopLevel: path.length < 1
          });
        }
      } catch (e) {
        /** We don't want this error to block renderer */
        /** We keep rendering the default content */
      }

      // Always return default content if anything goes wrong
      return renderExtension(children, layout, {
        isTopLevel: path.length < 1
      });
    };
    if (privateProps.__allowBodiedOverride) {
      return renderMultiBodiedExtension();
    } else {
      return jsx(React.Fragment, null, jsx(MultiBodiedExtensionNavigation, null, renderMultiBodiedExtension()), jsx(MultiBodiedExtensionChildrenContainer, null, children));
    }
  }, [loading, extensionContext, layout, path, marks, extensionKey, extensionType, parameters, content, localId, actions, children]);

  // eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
  var containerActiveFrameStyles = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n\t\t.multiBodiedExtension--frames\n\t\t\t> [data-extension-frame='true']:nth-of-type(", ") {\n\t\t\tdisplay: block;\n\t\t}\n\t"])), activeChildIndex + 1);
  return jsx("section", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: "multiBodiedExtension--container",
    css: [containerStyles, containerActiveFrameStyles],
    "data-testid": "multiBodiedExtension--container",
    "data-active-child-index": activeChildIndex,
    "data-layout": layout
  }, jsx(WidthConsumer, null, function (_ref4) {
    var width = _ref4.width;
    return jsx(MultiBodiedExtensionWrapper, {
      layout: layout,
      width: width,
      path: path
    }, renderContent());
  }));
};
export default MultiBodiedExtension;